import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {ICacheOptions, ICacheService} from '../../decorators/cacheable.directive';

@Injectable({
  providedIn: 'root',
})
export class CacheService implements ICacheService {
  private static _INSTANCE: ICacheService | undefined;

  static get INSTANCE(): ICacheService | undefined {
    return this._INSTANCE;
  }

  private readonly _syncCache = new Map<string, unknown>();
  private readonly _observableCache = new Map<string, Observable<unknown>>();

  private readonly _syncCacheLimit = new Map<string, string[]>();
  private readonly _observableCacheLimit = new Map<string, string[]>();

  constructor() {
    CacheService._INSTANCE = this;
  }

  addAndCheckLimit(options: ICacheOptions, key: string): void {
    if (options.limit && options.limit > 0) {
      const caches = this._syncCacheLimit.get(options.name) ?? [];
      this._syncCacheLimit.set(options.name, caches);
      caches.push(key);

      while (caches.length > options.limit) {
        this.deleteItem(caches.shift());
      }
    }
  }

  addAndCheckObservableLimit(options: ICacheOptions, key: string): void {
    if (options.limit && options.limit > 0) {
      const caches = this._observableCacheLimit.get(options.name) ?? [];
      this._observableCacheLimit.set(options.name, caches);
      caches.push(key);

      while (caches.length > options.limit) {
        this.deleteObservableItem(caches.shift());
      }
    }
  }

  clearItems(): void {
    this._syncCache.clear();
  }

  clearObservableItems(): void {
    this._observableCache.clear();
  }

  deleteItem(key: string): void {
    this._syncCache.delete(key);
  }

  deleteObservableItem(key: string): void {
    this._observableCache.delete(key);
  }

  getItem<T>(key: string): T | undefined {
    return this._syncCache.get(key) as T | undefined;
  }

  getObservableItem<T>(key: string): Observable<T> | undefined {
    return this._observableCache.get(key) as Observable<T> | undefined;
  }

  hasItem(key: string): boolean {
    return this._syncCache.has(key);
  }

  hasObservableItem(key: string): boolean {
    return this._observableCache.has(key);
  }

  getCacheNames(): string[] {
    return Array.from(this._syncCache.keys());
  }

  getObservableCacheNames(): string[] {
    return Array.from(this._observableCache.keys());
  }

  setItem<T>(key: string, item: T): void {
    this._syncCache.set(key, item);
  }

  setObservableItem<T>(
    key: string,
    item: Observable<T>,
    timeExpired?: number,
  ): void {
    this._observableCache.set(key, item);
    if (timeExpired) {
      setTimeout(() => {
        this.deleteObservableItem(key);
      }, timeExpired);
    }
  }
}

